import classNames from 'classnames';

import { ButtonOwnProps } from './button.model';

type ButtonClassNameProps = {
  hasIcon: boolean;
  onlyIcon: boolean;
};

const getButtonTextSizeClassName = (size: ButtonOwnProps['size']) =>
  size === 'sm' ? 'text-xs' : 'text-sm';

const getButtonSizeForOnlyIcon = (size: ButtonOwnProps['size']) =>
  size === 'sm' ? 'px-1 py-1' : 'px-3 py-3';

const getButtonSizeNoIcon = (size: ButtonOwnProps['size']) =>
  size === 'sm' ? 'px-2 py-2' : 'px-4 py-4';

const getButtonSizeWithIcon = (size: ButtonOwnProps['size']) =>
  size === 'sm' ? 'px-2 py-1' : 'px-4 py-3';

const getButtonColorClassName = (
  variant: ButtonOwnProps['variant']
): string => {
  switch (variant) {
    case 'light-teal':
      return 'bg-primary-100 text-primary-500 border-transparent';
    case 'emerald':
      return 'bg-emerald-500 text-white border-transparent';
    case 'outline':
      return 'text-primary-500 border-primary-500';
    case 'ghost':
      return 'text-primary-500 border-transparent';
    case 'teal':
    default:
      return 'bg-primary-500 text-white border-transparent';
  }
};

const getButtonFocusClassName = (
  variant: ButtonOwnProps['variant']
): string => {
  const defaultFocusClassName = 'focus:ring focus:ring-opacity-50';

  switch (variant) {
    case 'emerald':
      return `${defaultFocusClassName} focus:ring-emerald-500`;
    case 'outline':
    case 'ghost':
    case 'light-teal':
    case 'teal':
    default:
      return `${defaultFocusClassName} focus:ring-primary-500`;
  }
};

const getButtonHoverClassName = (
  variant: ButtonOwnProps['variant']
): string => {
  switch (variant) {
    case 'light-teal':
      return 'hover:bg-primary-500 hover:text-white';
    case 'emerald':
      return 'hover:bg-emerald-800';
    case 'outline':
    case 'ghost':
      return 'hover:bg-primary-100';
    case 'teal':
    default:
      return 'hover:bg-primary-800';
  }
};

export const getButtonClassName = (
  { variant = 'teal', size = 'md', disabled }: ButtonOwnProps,
  { hasIcon, onlyIcon }: ButtonClassNameProps
) =>
  classNames(
    'select-none focus:outline-none font-medium rounded border leading-4',
    getButtonTextSizeClassName(size),
    onlyIcon && getButtonSizeForOnlyIcon(size),
    !onlyIcon && !hasIcon && getButtonSizeNoIcon(size),
    !onlyIcon && hasIcon && getButtonSizeWithIcon(size),
    { 'opacity-50 cursor-default': disabled },
    getButtonColorClassName(variant),
    getButtonFocusClassName(variant),
    !disabled && getButtonHoverClassName(variant)
  );
